"use client";

import { useState } from "react";
import * as React from "react";
import { Path, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import { getLocaleFromPath } from "../../../lib/i18n/utils";
import { createContactSchema, ContactFormData } from "../../../lib/forms/contact-schema";

const STORAGE_KEY = 'contact-form-draft';
const MIN_SUBMIT_INTERVAL = 5000; // 5 seconds
const MIN_TIME_TO_SUBMIT_MS = 1200; // server enforces too; this is just UX

function isMeaningfulDraft(value: Partial<ContactFormData> | undefined): boolean {
  if (!value) return false;
  // Only persist when the user has actually typed something (or changed defaults).
  const hasTyped =
    !!value.name?.trim() ||
    !!value.email?.trim() ||
    !!value.phone?.trim() ||
    !!value.message?.trim() ||
    !!value.website?.trim();

  const hasChangedDefaults =
    (value.subject != null && value.subject !== "general_inquiry") ||
    (value.preferredContactMethod != null && value.preferredContactMethod !== "email");

  return hasTyped || hasChangedDefaults;
}

export function useContactForm() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const formRenderedAtRef = React.useRef<number>(Date.now());
  const draftSaveEnabledRef = React.useRef(true);
  
  const schema = createContactSchema(t);
  const form = useForm<ContactFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "general_inquiry",
      preferredContactMethod: "email",
      website: "",
      message: "",
    },
    mode: "onBlur",
  });

  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(null);
  const [lastSubmitTime, setLastSubmitTime] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Load draft from localStorage on mount
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const draft = localStorage.getItem(STORAGE_KEY);
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          form.reset(parsed);
        } catch (e) {
          // Ignore parse errors
          console.warn("Failed to parse draft from localStorage:", e);
        }
      }
    }
  }, [form]);

  // Save draft to localStorage on form changes
  React.useEffect(() => {
    const subscription = form.watch((value) => {
      if (typeof window !== "undefined") {
        if (!draftSaveEnabledRef.current) return;
        try {
          if (isMeaningfulDraft(value)) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } catch (e) {
          // Ignore storage errors (e.g., quota exceeded)
          console.warn("Failed to save draft to localStorage:", e);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Watch preferred contact method to revalidate phone when it changes
  const preferredContactMethod = form.watch("preferredContactMethod");
  React.useEffect(() => {
    if (preferredContactMethod === "phone") {
      form.trigger("phone");
    }
  }, [preferredContactMethod, form]);

  const onSubmit = async (data: ContactFormData) => {
    // Rate limiting check
    const now = Date.now();
    if (lastSubmitTime && now - lastSubmitTime < MIN_SUBMIT_INTERVAL) {
      const remainingSeconds = Math.ceil((MIN_SUBMIT_INTERVAL - (now - lastSubmitTime)) / 1000);
      setErrorMessage(t("Please wait {{seconds}} seconds before submitting again.", { seconds: remainingSeconds }));
      setSubmitStatus("error");
      return;
    }

    // Soft UX guard: if user submits instantly, ask them to retry.
    // (Server will also enforce; this reduces confusing server errors.)
    const timeToSubmitMs = now - formRenderedAtRef.current;
    if (timeToSubmitMs < MIN_TIME_TO_SUBMIT_MS) {
      setErrorMessage(t("Please wait {{seconds}} seconds before submitting again.", { seconds: 2 }));
      setSubmitStatus("error");
      return;
    }

    setSubmitStatus(null);
    setErrorMessage(null);
    setLastSubmitTime(now);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name.trim(),
          email: data.email.trim(),
          phone: data.phone?.trim() || "",
          subject: data.subject.trim(),
          preferredContactMethod: data.preferredContactMethod,
          website: data.website?.trim() || "",
          message: data.message.trim(),
          locale: locale,
          localizations: [],
          formRenderedAt: formRenderedAtRef.current,
        }),
      });

      const responseData: unknown = await response.json();

      if (!response.ok) {
        // Try to extract specific error message
        const responseObj = (typeof responseData === "object" && responseData !== null)
          ? (responseData as Record<string, unknown>)
          : null;
        const errorMsg =
          (typeof responseObj?.error === "string" && responseObj.error) ||
          (typeof responseObj?.message === "string" && responseObj.message) ||
          t("Failed to submit form");
        
        // Set field-specific errors if available
        const fieldErrors = responseObj?.errors;
        if (typeof fieldErrors === "object" && fieldErrors !== null) {
          Object.entries(fieldErrors as Record<string, unknown>).forEach(([field, message]) => {
            if (typeof message !== "string") return;
            // RHF expects a typed Path; we only set errors for known fields.
            if (field in form.getValues()) {
              form.setError(field as Path<ContactFormData>, { message });
            }
          });
        }
        
        setErrorMessage(errorMsg);
        throw new Error(errorMsg);
      }

      // Clear draft on successful submit
      if (typeof window !== "undefined") {
        // Prevent the watcher from immediately re-saving a draft during reset.
        draftSaveEnabledRef.current = false;
        localStorage.removeItem(STORAGE_KEY);
      }
      
      setSubmitStatus("success");
      form.reset();
      formRenderedAtRef.current = Date.now();

      // Re-enable draft saving on next tick (after reset settles).
      if (typeof window !== "undefined") {
        setTimeout(() => {
          draftSaveEnabledRef.current = true;
        }, 0);
      } else {
        draftSaveEnabledRef.current = true;
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
      if (!errorMessage) {
        setErrorMessage(error instanceof Error ? error.message : t("An error occurred while sending. Please try again."));
      }
    }
  };

  return {
    form,
    preferredContactMethod,
    submitStatus,
    onSubmit,
    errorMessage,
  };
}

